import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  AttendanceCourseSummary,
  Class,
  COURSE_PROPOSAL_STATUS,
  CourseProposal,
  Semester,
  Student,
  Teacher,
} from "../models";
import { formatClassForMobile } from "../utils/class-response";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

interface AuthRequest extends Request {
  user?: { userId: string; role: string; schoolId?: string };
}

class CourseModuleController {
  async createCourseProposal(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const {
        courseCode,
        courseName,
        courseType,
        department,
        modality,
        creditHours,
        description,
        semesterId,
      } = req.body;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      if (!courseCode || !courseName) {
        res.status(400).json({
          success: false,
          message: "courseCode and courseName are required",
        });
        return;
      }

      const proposal = await CourseProposal.create({
        schoolId: teacher.schoolId,
        teacherId: teacher._id,
        semesterId: semesterId
          ? mongoose.Types.ObjectId.createFromHexString(String(semesterId))
          : undefined,
        courseCode: String(courseCode).trim().toUpperCase(),
        courseName: String(courseName).trim(),
        courseType,
        department,
        modality: modality ?? "on_campus",
        creditHours,
        description,
        status: COURSE_PROPOSAL_STATUS.PENDING,
      });

      res.status(201).json({
        success: true,
        message: "Course proposal submitted successfully",
        data: { proposal: { id: proposal.id, status: proposal.status } },
      } as APIResponse);
    } catch (error) {
      logger.error("Create course proposal error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit course proposal",
      } as APIResponse);
    }
  }

  async getTeacherCourses(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const {
        semesterId,
        modality,
        department,
        courseType,
        page = 1,
        limit = 20,
      } = req.query;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const classFilter: Record<string, unknown> = {
        teacherIds: teacher._id,
        schoolId: teacher.schoolId,
        status: "active",
        isActive: true,
      };
      if (semesterId)
        classFilter.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      if (modality) classFilter.modality = modality;
      if (department)
        classFilter.department = { $regex: department, $options: "i" };
      if (courseType)
        classFilter.courseType = { $regex: courseType, $options: "i" };

      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      const [classes, total, proposals] = await Promise.all([
        Class.find({ ...classFilter })
          .populate("semesterId", "semesterName academicYear startDate endDate")
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Class.countDocuments({ ...classFilter }),
        CourseProposal.find({
          teacherId: teacher._id,
          status: COURSE_PROPOSAL_STATUS.PENDING,
        })
          .sort({ createdAt: -1 })
          .lean(),
      ]);

      res.status(200).json({
        success: true,
        message: "Teacher courses retrieved successfully",
        data: {
          courses: classes.map(c =>
            formatClassForMobile(c as Record<string, unknown>),
          ),
          pendingProposals: proposals.map(p => ({
            id: p.id,
            courseCode: p.courseCode,
            courseName: p.courseName,
            modality: p.modality,
            status: p.status,
            createdAt: p.createdAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get teacher courses error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve teacher courses",
      } as APIResponse);
    }
  }

  async getTeacherCourseById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { courseId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const classItem = await Class.findOne({
        _id: courseId,
        teacherIds: teacher._id,
      })
        .populate("semesterId", "semesterName academicYear startDate endDate")
        .populate("studentIds", "fullName studentId profilePicture")
        .lean();

      if (!classItem) {
        res.status(404).json({ success: false, message: "Course not found" });
        return;
      }

      res.status(200).json({
        success: true,
        message: "Course detail retrieved successfully",
        data: {
          course: formatClassForMobile(classItem as Record<string, unknown>),
          enrollments: (classItem.studentIds as unknown[] | undefined) ?? [],
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get teacher course detail error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve course detail",
      } as APIResponse);
    }
  }

  async getStudentCourses(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const {
        semesterId,
        department,
        teacherId,
        modality,
        page = 1,
        limit = 20,
      } = req.query;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const currentSemester = await Semester.findOne({
        schoolId: student.schoolId,
        isActive: true,
        startDate: { $lte: new Date() },
        endDate: { $gte: new Date() },
      }).lean();

      const catalogFilter: Record<string, unknown> = {
        schoolId: student.schoolId,
        status: "active",
        isActive: true,
      };

      if (currentSemester && !semesterId) {
        catalogFilter.semesterId = currentSemester._id;
      } else if (semesterId) {
        catalogFilter.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      if (modality) catalogFilter.modality = modality;
      if (department)
        catalogFilter.department = { $regex: department, $options: "i" };
      if (teacherId)
        catalogFilter.teacherIds = new mongoose.Types.ObjectId(
          teacherId as string,
        );

      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      const [classes, total] = await Promise.all([
        Class.find({ ...catalogFilter })
          .populate("semesterId", "semesterName startDate endDate")
          .populate("teacherIds", "fullName employeeId profilePicture")
          .sort({ className: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Class.countDocuments({ ...catalogFilter }),
      ]);

      const enrolledRows = await Class.find({ studentIds: student._id })
        .select("_id")
        .lean();
      const enrolledClassIds = new Set(enrolledRows.map(c => c._id.toString()));

      res.status(200).json({
        success: true,
        message: "Courses retrieved successfully",
        data: {
          courses: classes.map(c => ({
            ...formatClassForMobile(c as Record<string, unknown>),
            teachers: c.teacherIds,
            isEnrolled: enrolledClassIds.has(c._id.toString()),
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student courses error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve courses",
      } as APIResponse);
    }
  }

  async getStudentCourseById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { courseId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const classItem = await Class.findOne({
        _id: courseId,
        schoolId: student.schoolId,
        status: "active",
        isActive: true,
      })
        .populate("semesterId", "semesterName startDate endDate")
        .populate("teacherIds", "fullName employeeId profilePicture")
        .lean();

      if (!classItem) {
        res.status(404).json({ success: false, message: "Course not found" });
        return;
      }

      const isEnrolled = classItem.studentIds?.some(
        id => id.toString() === student._id.toString(),
      );

      res.status(200).json({
        success: true,
        message: "Course detail retrieved successfully",
        data: {
          course: {
            ...formatClassForMobile(classItem as Record<string, unknown>),
            teachers: classItem.teacherIds,
            isEnrolled,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student course detail error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve course detail",
      } as APIResponse);
    }
  }

  async createEnrollRequest(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { courseId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const classItem = await Class.findOne({
        _id: courseId,
        schoolId: student.schoolId,
        status: "active",
        isActive: true,
      });

      if (!classItem) {
        res.status(404).json({ success: false, message: "Course not found" });
        return;
      }

      if (classItem.isStudentEnrolled(student._id)) {
        res.status(409).json({
          success: false,
          message: "Already enrolled in this course",
        });
        return;
      }

      const semester = await Semester.findById(classItem.semesterId).lean();
      if (semester && semester.startDate > new Date()) {
        res.status(422).json({
          success: false,
          message: "Cannot enroll in future semester courses",
        });
        return;
      }

      await classItem.enrollStudent(student._id);

      res.status(201).json({
        success: true,
        message: "Enrollment request completed successfully",
        data: { classId: classItem.id, studentId: student.id },
      } as APIResponse);
    } catch (error) {
      logger.error("Student enroll request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to process enrollment request",
      } as APIResponse);
    }
  }
}

export const courseModuleController = new CourseModuleController();
